import { ReactNode, useState } from 'react';
import { FormattedDate } from 'react-intl';

import { styled } from 'styled-components';

import { MessageKey, t } from 'translations';
import { LinkButton } from 'ui';
import { GeneralDialog } from 'ui/components/GeneralDialog';
import { HeaderText, Link, SmallText } from 'ui/styles/text';

import { BillingPeriod, RecurringFee } from '../types';
import { getMonetaryValue } from '../utils/helpers';

const TextContainer = styled.div`
  padding: 15px 25px;
`;

const SupportContainer = styled.div`
  border-radius: 12px;
  background: #555656;
  padding: 16px 16px;
`;

const TermsContainer = styled.div`
  border-radius: 12px;
  background: rgba(21, 22, 25, 1);
  padding: 16px 16px;
`;

interface GetTermsTextParams {
  selectedPlan: RecurringFee;
  setDialogOpen: (open: boolean) => void;
  nextPaymentDate?: Date;
}

const getTermsText = ({ selectedPlan, setDialogOpen, nextPaymentDate }: GetTermsTextParams): ReactNode => {
  const termsKey =
    selectedPlan.billingPeriod === BillingPeriod.Years
      ? 'membership_hub_terms_product_selected_annual'
      : 'membership_hub_terms_product_selected_monthly';
  return t(termsKey, {
    billingAmount: getMonetaryValue(selectedPlan.amount, selectedPlan.currency),
    billingDate: <FormattedDate value={nextPaymentDate} format="default" />,
    TermsDialog: (linkText) => (
      <LinkButton onClick={() => setDialogOpen(true)} $fontSize="14px" $color="helsinkiBlue" type="button">
        {linkText}
      </LinkButton>
    ),
  });
};

const supportText = t('membership_hub_terms_dialog_support_section', {
  SupportPage: (linkText) => (
    <Link
      href="https://support.ouraring.com/hc/en-us/articles/34172788742035-Activating-a-Prepaid-Oura-Membership"
      target="_blank"
      $fontSize="16px"
      $decoration="underline"
      $color="helsinkiBlue"
    >
      {linkText}
    </Link>
  ),
});
const termsLastParagraph = t('membership_hub_terms_5', {
  TermsAndConditions: (linkText) => (
    <Link
      href="https://ouraring.com/terms-and-conditions"
      target="_blank"
      $fontSize="16px"
      $decoration="underline"
      $color="helsinkiBlue"
    >
      {linkText}
    </Link>
  ),
});

const termsDialogTranslations: MessageKey[] = [
  'membership_hub_terms_1',
  'membership_hub_terms_2',
  'membership_hub_terms_3',
  'membership_hub_terms_4',
];
interface SimpleDialogProps {
  simpleTerms: true;
}
interface FullDialogProps {
  simpleTerms: false;
  selectedPlan: RecurringFee;
  nextPaymentDate?: Date;
}

export const TermsDialog = (dialogProps: SimpleDialogProps | FullDialogProps) => {
  const [isDialogOpen, setDialogOpen] = useState(false);
  return (
    <>
      {!dialogProps.simpleTerms ? (
        <TermsContainer data-testid={`terms-${dialogProps.selectedPlan.billingPeriod}`}>
          <SmallText $textAlign="left" $fontSize="small">
            {getTermsText({
              selectedPlan: dialogProps.selectedPlan,
              setDialogOpen,
              nextPaymentDate: dialogProps.nextPaymentDate,
            })}
          </SmallText>
        </TermsContainer>
      ) : (
        <SmallText $textAlign="left" $fontSize="small">
          <LinkButton onClick={() => setDialogOpen(true)} $fontSize="14px" $color="helsinkiBlue" type="button">
            {t('membership_hub_membership_terms')}
          </LinkButton>
        </SmallText>
      )}
      <GeneralDialog isOpen={isDialogOpen} onClose={() => setDialogOpen(false)}>
        <TextContainer>
          <HeaderText $textAlign="center" $fontSize="xxxlarge" $margin="0 0 25px">
            {t('membership_hub_membership_terms')}
          </HeaderText>
          {termsDialogTranslations.map((translationKey) => (
            <SmallText $textAlign="center" $fontSize="medium" $margin="0 0 25px" key={translationKey}>
              {t(translationKey)}
            </SmallText>
          ))}
          <SmallText $textAlign="center" $fontSize="medium" $margin="0 0 25px">
            {termsLastParagraph}
          </SmallText>
          <SupportContainer>
            <SmallText $textAlign="center" $fontSize="medium">
              {supportText}
            </SmallText>
          </SupportContainer>
        </TextContainer>
      </GeneralDialog>
    </>
  );
};
